import { useMemo, useState, type ReactNode } from 'react';
import { App, Button, Card, Checkbox, ColorPicker, Input, Segmented, Select, Slider, Space, Switch, Tooltip, Typography, Upload } from 'antd';
import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  BgColorsOutlined,
  ColumnHeightOutlined,
  CopyOutlined,
  DeleteOutlined,
  DesktopOutlined,
  FontSizeOutlined,
  LineOutlined,
  LinkOutlined,
  MobileOutlined,
  PictureOutlined,
  UploadOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import type { AgentItem, BlockPadding, EmailBlock, EmailBlockType, EmailDesign, EmailStyle, MergeContext, ProjectItem } from '@crm/shared';
import { BLOCK_LABELS, EMAIL_FONTS, defaultBlockPadding, readableOn, projectEmailStyle, SIGNATURE_SLOT, isUsableLink, newBlockId, newEmailBlock, renderEmailDesign, renderMergeTags } from '@crm/shared';
import { RichTextEditor } from '../RichTextEditor';
import { EmailPreview } from './EmailPreview';
import { ImageCropper } from './ImageCropper';
import { baseURL, errorMessage } from '../../api/client';
import { useUploadEmailImage } from '../../features/campaigns/api';
import { palette } from '../../app/theme';

/** Origin uploads are served from; '' when the API shares the app's origin. */
export const ASSET_BASE = baseURL.replace(/\/api$/, '');

const BLOCK_ICONS: Record<EmailBlockType, ReactNode> = {
  heading: <FontSizeOutlined />,
  text: <AlignLeftOutlined />,
  button: <LinkOutlined />,
  image: <PictureOutlined />,
  divider: <LineOutlined />,
  spacer: <ColumnHeightOutlined />,
};

type Align = 'left' | 'center' | 'right';
const ALIGN_OPTIONS = [
  { value: 'left', icon: <AlignLeftOutlined />, title: 'Left' },
  { value: 'center', icon: <AlignCenterOutlined />, title: 'Centre' },
  { value: 'right', icon: <AlignRightOutlined />, title: 'Right' },
];

const strip = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

function summary(b: EmailBlock): string {
  switch (b.type) {
    case 'heading':
      return b.text || 'Empty heading';
    case 'text':
      return strip(b.html) || 'Empty text';
    case 'button':
      return `${b.label || 'Button'} → ${b.url || 'no link'}`;
    case 'image':
      return b.alt || (b.src ? 'Picture' : 'No picture yet');
    case 'divider':
      return 'Divider';
    case 'spacer':
      return `Space · ${b.height}px`;
  }
}

/** Renders a design as it will arrive: signature in place and, optionally, tags filled for a sample contact. */
export function previewHtml(design: EmailDesign, opts: { agent?: AgentItem | null; sample?: MergeContext['contact']; selectedId?: string | null; editor?: boolean }) {
  const html = renderEmailDesign(design, { assetBase: ASSET_BASE, editor: opts.editor, selectedId: opts.selectedId });
  const signature = opts.agent?.signatureHtml ? `<div style="margin-top:16px">${opts.agent.signatureHtml}</div>` : '';
  const withSignature = html.replace(SIGNATURE_SLOT, signature);
  if (!opts.sample) return withSignature;
  return renderMergeTags(withSignature, { contact: opts.sample, agent: opts.agent ?? undefined, extra: { unsubscribe_url: '#' } });
}

/** A project's email branding (logo, button and link colour), for the "Use project branding" button. */
export interface EmailBrand {
  name: string;
  style: Partial<EmailStyle>;
}
export function projectBrand(p: ProjectItem | null | undefined): EmailBrand | undefined {
  const style = projectEmailStyle(p);
  return p && Object.keys(style).length ? { name: p.name, style } : undefined;
}

interface Props {
  design: EmailDesign;
  /** the campaign's project branding; the style panel offers to apply it */
  brand?: EmailBrand;
  onChange: (d: EmailDesign) => void;
  agent?: AgentItem | null;
  /** contact used for "Show tags filled in"; the toggle is hidden without one */
  sample?: MergeContext['contact'] | null;
}

/** Block-based email designer: live preview on the left, blocks and email style on the right. */
export function EmailDesigner({ design, onChange, agent, sample, brand }: Props) {
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [filled, setFilled] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const html = useMemo(() => previewHtml(design, { agent, sample: filled ? sample : null, selectedId, editor: true }), [design, agent, sample, filled, selectedId]);

  const setBlocks = (blocks: EmailBlock[]) => onChange({ ...design, blocks });
  const setStyle = (patch: Partial<EmailStyle>) => onChange({ ...design, style: { ...design.style, ...patch } });
  const updateBlock = (id: string, patch: Partial<EmailBlock>) => setBlocks(design.blocks.map((b) => (b.id === id ? ({ ...b, ...patch } as EmailBlock) : b)));
  const add = (type: EmailBlockType) => {
    const block = newEmailBlock(type);
    const at = selectedId ? design.blocks.findIndex((b) => b.id === selectedId) + 1 : design.blocks.length;
    setBlocks([...design.blocks.slice(0, at), block, ...design.blocks.slice(at)]);
    setSelectedId(block.id);
  };
  const move = (i: number, by: number) => {
    const next = [...design.blocks];
    [next[i], next[i + by]] = [next[i + by], next[i]];
    setBlocks(next);
  };
  const duplicate = (i: number) => {
    const copy = { ...design.blocks[i], id: newBlockId() } as EmailBlock;
    setBlocks([...design.blocks.slice(0, i + 1), copy, ...design.blocks.slice(i + 1)]);
    setSelectedId(copy.id);
  };
  const remove = (i: number) => {
    if (design.blocks[i].id === selectedId) setSelectedId(null);
    setBlocks(design.blocks.filter((_, j) => j !== i));
  };
  const selectFromPreview = (id: string) => {
    setSelectedId(id);
    document.getElementById(`email-block-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  return (
    <div className="email-designer">
      <div className="email-designer-preview">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <Segmented
            aria-label="Preview size"
            value={device}
            onChange={(v) => setDevice(v as 'desktop' | 'mobile')}
            options={[
              { value: 'desktop', label: 'Desktop', icon: <DesktopOutlined /> },
              { value: 'mobile', label: 'Mobile', icon: <MobileOutlined /> },
            ]}
          />
          {sample && (
            <Checkbox checked={filled} onChange={(e) => setFilled(e.target.checked)}>
              Show tags filled in for a sample recipient
            </Checkbox>
          )}
        </div>
        <EmailPreview html={html} width={device === 'desktop' ? 600 : 375} onSelect={selectFromPreview} />
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
          Click any part of the email to edit it. The unsubscribe link at the bottom is added to every email automatically.
        </Typography.Text>
      </div>

      <div className="email-designer-side">
        <Card size="small" title="Add a block">
          <div className="email-block-palette">
            {(Object.keys(BLOCK_LABELS) as EmailBlockType[]).map((t) => (
              <button key={t} type="button" className="email-block-add" onClick={() => add(t)}>
                <span style={{ fontSize: 18 }}>{BLOCK_ICONS[t]}</span>
                {BLOCK_LABELS[t]}
              </button>
            ))}
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{selectedId ? 'Added below the selected block.' : 'Added at the end.'}</Typography.Text>
        </Card>

        <Card size="small" title={`Blocks (${design.blocks.length})`}>
          {!design.blocks.length && <Typography.Text type="secondary">No blocks yet. Add one above.</Typography.Text>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {design.blocks.map((b, i) => {
              const open = b.id === selectedId;
              return (
                <div key={b.id} id={`email-block-${b.id}`} className={`email-block-row${open ? ' is-open' : ''}`}>
                  <div className="email-block-head" role="button" tabIndex={0} aria-expanded={open} onClick={() => setSelectedId(open ? null : b.id)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setSelectedId(open ? null : b.id))}>
                    <span style={{ color: palette.textSecondary }}>{BLOCK_ICONS[b.type]}</span>
                    <span className="email-block-summary">{summary(b)}</span>
                    <span onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flex: 'none' }}>
                      <Tooltip title="Move up"><Button size="small" type="text" icon={<ArrowUpOutlined />} aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)} /></Tooltip>
                      <Tooltip title="Move down"><Button size="small" type="text" icon={<ArrowDownOutlined />} aria-label="Move down" disabled={i === design.blocks.length - 1} onClick={() => move(i, 1)} /></Tooltip>
                      <Tooltip title="Duplicate"><Button size="small" type="text" icon={<CopyOutlined />} aria-label="Duplicate" onClick={() => duplicate(i)} /></Tooltip>
                      <Tooltip title="Delete"><Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="Delete" onClick={() => remove(i)} /></Tooltip>
                    </span>
                  </div>
                  {open && (
                    <div className="email-block-body">
                      <BlockEditor block={b} onChange={(patch) => updateBlock(b.id, patch)} style={design.style} />
                      <RowFields block={b} onChange={(patch) => updateBlock(b.id, patch)} emailBackground={design.style.emailBackground} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        <StylePanel style={design.style} onChange={setStyle} brand={brand} />
      </div>
    </div>
  );
}

function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>{label}</Typography.Text>
      {children}
      {hint && <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>{hint}</Typography.Text>}
    </div>
  );
}

function AlignField({ value, onChange }: { value: Align; onChange: (a: Align) => void }) {
  return (
    <Field label="Alignment">
      <Segmented value={value} onChange={(v) => onChange(v as Align)} options={ALIGN_OPTIONS.map((o) => ({ value: o.value, icon: o.icon, title: o.title }))} />
    </Field>
  );
}

function LinkInput({ value, onChange, placeholder, optional }: { value: string; onChange: (v: string) => void; placeholder?: string; optional?: boolean }) {
  const bad = optional ? !!value.trim() && !isUsableLink(value) : !isUsableLink(value);
  return (
    <>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder ?? 'https://'} status={bad ? 'error' : undefined} />
      {bad && <Typography.Text type="danger" style={{ fontSize: 12 }}>Use a full link starting with https://</Typography.Text>}
    </>
  );
}

/** Uploads a picture; with `crop`, it first goes through the cropper (GIFs skip it so animations survive). */
function ImagePicker({ value, onChange, label = 'Upload picture', crop }: { value: string; onChange: (src: string) => void; label?: string; crop?: boolean }) {
  const { message } = App.useApp();
  const upload = useUploadEmailImage();
  const [pending, setPending] = useState<File | null>(null);
  const send = (file: File) =>
    upload
      .mutateAsync(file)
      .then((r) => {
        onChange(r.url);
        setPending(null);
      })
      .catch((e) => message.error(errorMessage(e)));
  return (
    <>
      <Upload
        accept="image/*"
        showUploadList={false}
        beforeUpload={(file) => {
          if (crop && file.type !== 'image/gif') setPending(file);
          else void send(file);
          return false;
        }}
      >
        <Button icon={<UploadOutlined />} loading={upload.isPending}>{value ? 'Replace' : label}</Button>
      </Upload>
      {crop && <ImageCropper file={pending} onCancel={() => setPending(null)} onDone={(f) => void send(f)} confirmLoading={upload.isPending} />}
    </>
  );
}

function BlockEditor({ block: b, onChange, style }: { block: EmailBlock; onChange: (patch: Partial<EmailBlock>) => void; style: EmailStyle }) {
  const textColor = style.textColor;
  const colorField = (b: { color?: string }) => (
    <ColorField label="Text colour" value={b.color ?? textColor} onChange={(color) => onChange({ color })} onReset={b.color ? () => onChange({ color: undefined }) : undefined} />
  );
  switch (b.type) {
    case 'heading':
      return (
        <>
          <Field label="Text" hint="Merge tags work here, e.g. {{first_name|there}}.">
            <Input value={b.text} onChange={(e) => onChange({ text: e.target.value })} autoFocus />
          </Field>
          <Field label="Size">
            <Segmented value={b.level} onChange={(v) => onChange({ level: v as 1 | 2 | 3 })} options={[{ value: 1, label: 'Large' }, { value: 2, label: 'Medium' }, { value: 3, label: 'Small' }]} />
          </Field>
          {colorField(b)}
          <AlignField value={b.align} onChange={(align) => onChange({ align })} />
        </>
      );
    case 'text':
      return (
        <>
          <Field label="Text">
            <RichTextEditor key={b.id} value={b.html} onChange={(html) => onChange({ html })} minHeight={120} />
          </Field>
          {colorField(b)}
          <AlignField value={b.align} onChange={(align) => onChange({ align })} />
        </>
      );
    case 'button':
      return (
        <>
          <Field label="Label"><Input value={b.label} onChange={(e) => onChange({ label: e.target.value })} autoFocus /></Field>
          <Field label="Link"><LinkInput value={b.url} onChange={(url) => onChange({ url })} /></Field>
          <ColorField label="Button colour" value={b.buttonColor ?? style.accentColor} onChange={(buttonColor) => onChange({ buttonColor })} onReset={b.buttonColor ? () => onChange({ buttonColor: undefined }) : undefined} resetLabel="Use the email's button colour" />
          <ColorField
            label="Button text colour"
            value={b.labelColor ?? (b.buttonColor ? readableOn(b.buttonColor) : style.buttonTextColor)}
            onChange={(labelColor) => onChange({ labelColor })}
            onReset={b.labelColor ? () => onChange({ labelColor: undefined }) : undefined}
            resetLabel={b.buttonColor ? 'Pick a readable colour automatically' : "Use the email's button text colour"}
          />
          <AlignField value={b.align} onChange={(align) => onChange({ align })} />
          <Space><Switch size="small" checked={!!b.fullWidth} onChange={(fullWidth) => onChange({ fullWidth })} /><Typography.Text>Full width</Typography.Text></Space>
        </>
      );
    case 'image':
      return (
        <>
          <Field label="Picture" hint="JPG or PNG: you can crop it to a 1200 × 500 banner and choose the compression. GIFs upload as they are.">
            <Space wrap>
              <ImagePicker crop value={b.src} onChange={(src) => onChange({ src })} />
              {b.src && <Button type="link" danger onClick={() => onChange({ src: '' })}>Remove</Button>}
            </Space>
            <Input style={{ marginTop: 8 }} value={b.src} onChange={(e) => onChange({ src: e.target.value })} placeholder="…or paste a picture address (https://…)" />
          </Field>
          <Field label="Description" hint="Shown when the picture does not load, and read out by screen readers."><Input value={b.alt} onChange={(e) => onChange({ alt: e.target.value })} /></Field>
          <Field label="Link (optional)"><LinkInput optional value={b.href ?? ''} onChange={(href) => onChange({ href })} placeholder="Where a click on the picture goes" /></Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
            <Switch size="small" checked={!!b.fullBleed} onChange={(fullBleed) => onChange({ fullBleed })} />
            <Typography.Text>Edge to edge (full width, no white space around it)</Typography.Text>
          </label>
          {!b.fullBleed && (
            <>
              <Field label={`Width · ${b.width}%`}><Slider min={10} max={100} step={5} value={b.width} onChange={(width) => onChange({ width })} /></Field>
              <AlignField value={b.align} onChange={(align) => onChange({ align })} />
            </>
          )}
        </>
      );
    case 'divider':
      return <Typography.Text type="secondary">A thin line across the email.</Typography.Text>;
    case 'spacer':
      return <Field label={`Height · ${b.height}px`}><Slider min={4} max={160} step={4} value={b.height} onChange={(height) => onChange({ height })} /></Field>;
  }
}

/** The block's row in the email: its background and the space around its content. */
function RowFields({ block: b, onChange, emailBackground }: { block: EmailBlock; onChange: (patch: Partial<EmailBlock>) => void; emailBackground: string }) {
  const pad = b.padding ?? defaultBlockPadding(b);
  const setPad = (patch: Partial<BlockPadding>) => onChange({ padding: { ...pad, ...patch } });
  const slider = (label: string, key: keyof BlockPadding, max: number) => (
    <Field label={`${label} · ${pad[key]}px`}>
      <Slider min={0} max={max} step={4} value={pad[key]} onChange={(v) => setPad({ [key]: v })} />
    </Field>
  );
  return (
    <div style={{ borderTop: '1px solid #ECEEF2', marginTop: 12, paddingTop: 12 }}>
      <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Row</Typography.Text>
      <ColorField label="Background" value={b.background ?? emailBackground} onChange={(background) => onChange({ background })} onReset={b.background ? () => onChange({ background: undefined }) : undefined} resetLabel="Use the email background" />
      {slider('Space above', 'top', 120)}
      {slider('Space below', 'bottom', 120)}
      {slider('Space left and right', 'sides', 80)}
      {b.padding && <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={() => onChange({ padding: undefined })}>Use the default spacing</Button>}
    </div>
  );
}

function ColorField({ label, value, onChange, onReset, resetLabel = "Use the email's text colour" }: { label: string; value: string; onChange: (v: string) => void; onReset?: () => void; resetLabel?: string }) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <Field label={label} hint={onReset ? <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={onReset}>{resetLabel}</Button> : undefined}>
      <Space.Compact style={{ width: '100%' }}>
        <ColorPicker value={value} disabledAlpha onChange={(c) => onChange(c.toHexString())} />
        <Input
          aria-label={label}
          value={draft ?? value}
          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
          onChange={(e) => {
            const v = e.target.value.trim();
            setDraft(v);
            if (/^#[0-9a-fA-F]{6}$/.test(v)) onChange(v.toLowerCase());
          }}
          onBlur={() => setDraft(null)}
          status={draft && !/^#[0-9a-fA-F]{6}$/.test(draft) ? 'error' : undefined}
        />
      </Space.Compact>
    </Field>
  );
}

function StylePanel({ style: s, onChange, brand }: { style: EmailStyle; onChange: (patch: Partial<EmailStyle>) => void; brand?: EmailBrand }) {
  const matches = !!brand && Object.entries(brand.style).every(([k, v]) => s[k as keyof EmailStyle] === v);
  return (
    <Card size="small" title={<Space><BgColorsOutlined />Email style</Space>}>
      {brand && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12, padding: '8px 10px', background: palette.canvas, borderRadius: 8 }}>
          <Space size={6}>
            {brand.style.accentColor && <span style={{ width: 14, height: 14, borderRadius: 4, background: brand.style.accentColor, display: 'inline-block' }} />}
            <Typography.Text style={{ fontSize: 13 }}>{matches ? `Matches ${brand.name}'s branding` : `${brand.name} branding`}</Typography.Text>
          </Space>
          {!matches && <Button size="small" onClick={() => onChange(brand.style)}>Use {brand.style.logoUrl && brand.style.accentColor ? 'logo and colours' : brand.style.accentColor ? 'colours' : 'logo'}</Button>}
        </div>
      )}
      <Field label="Logo (top of the email)">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, border: `1px solid ${palette.border}`, borderRadius: 8, padding: 10 }}>
          <div style={{ width: 88, height: 56, display: 'grid', placeItems: 'center', background: palette.canvas, borderRadius: 6, overflow: 'hidden', flex: 'none' }}>
            {s.logoUrl ? <img src={s.logoUrl.startsWith('/') ? `${ASSET_BASE}${s.logoUrl}` : s.logoUrl} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%' }} /> : <FileTextOutlined style={{ color: palette.textTertiary, fontSize: 20 }} />}
          </div>
          <Space orientation="vertical" size={4}>
            <ImagePicker value={s.logoUrl} onChange={(logoUrl) => onChange({ logoUrl })} label="Upload logo" />
            {s.logoUrl && <Button size="small" type="link" danger style={{ padding: 0 }} onClick={() => onChange({ logoUrl: '' })}>Remove</Button>}
          </Space>
        </div>
      </Field>
      {s.logoUrl && <Field label={`Logo width · ${s.logoWidth}px`}><Slider min={40} max={560} step={10} value={s.logoWidth} onChange={(logoWidth) => onChange({ logoWidth })} /></Field>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
        <ColorField label="Buttons" value={s.accentColor} onChange={(accentColor) => onChange({ accentColor })} />
        <ColorField label="Links" value={s.linkColor ?? s.accentColor} onChange={(linkColor) => onChange({ linkColor })} onReset={s.linkColor ? () => onChange({ linkColor: undefined }) : undefined} resetLabel="Use the button colour" />
        <ColorField label="Button text" value={s.buttonTextColor} onChange={(buttonTextColor) => onChange({ buttonTextColor })} />
        <ColorField label="Text" value={s.textColor} onChange={(textColor) => onChange({ textColor })} />
        <ColorField label="Email background" value={s.emailBackground} onChange={(emailBackground) => onChange({ emailBackground })} />
        <ColorField label="Page background" value={s.pageBackground} onChange={(pageBackground) => onChange({ pageBackground })} />
      </div>
      <Field label="Font">
        <Select style={{ width: '100%' }} value={s.font} onChange={(font) => onChange({ font })} options={Object.entries(EMAIL_FONTS).map(([value, f]) => ({ value, label: <span style={{ fontFamily: f.stack }}>{f.label}</span> }))} />
      </Field>
      <Field label="Footer text" hint="Who is sending and why the reader gets this. The unsubscribe link is added below it.">
        <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} value={s.footerText} onChange={(e) => onChange({ footerText: e.target.value })} placeholder="You are receiving this email from …" />
      </Field>
      <Space><Switch size="small" checked={s.showSignature} onChange={(showSignature) => onChange({ showSignature })} /><Typography.Text>Add the sender's signature</Typography.Text></Space>
    </Card>
  );
}
